import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TextareaAutosize from 'react-textarea-autosize';
import { ImageIcon, Video, X } from 'lucide-react';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { openMainModal } from '../../../store/actions/profileActions';
import { generateProfilePostText, createProfilePost } from '../../../store/actions';
import useUserImage from '../../../hooks/useUserImage.js';
import { fireAnalytics } from '../../../utils/firebase';
import { getSessionId, getDeviceCategory } from '../../../utils/analyticsHelpers.ts';
import styles from './ProfilePostSuggestionModal.module.scss';
import StarterKitThumbnailSelector from '../FeedModal/StarterKitThumbnailSelector.tsx';
import StarterKitImagePicker from '../FeedModal/StarterKitImagePicker.tsx';
import {
  canPublishWithStarterRule,
  type PublicationAttachedImage,
} from '../../../types/publicationMedia.ts';
import {
  canRequestProfilePostGeneration,
  recordSuccessfulProfilePostGeneration,
  formatProfilePostRetryMinutes,
} from '../../../utils/profilePostGenerationLimit.ts';

function profilePostModalGetSkillName(skill: any): string {
  if (typeof skill === 'string') {
    return skill;
  }
  if (skill?.skill?.name) {
    return skill.skill.name;
  }
  if (skill?.name) {
    return skill.name;
  }
  return '';
}

function profilePostModalHasOnlyOtrosAsSkill(userData: any): boolean {
  if (!userData?.skills?.length) return false;
  const skillNames = userData.skills.map(profilePostModalGetSkillName)
    .filter((s: string) => s && s.trim() !== '');
  return skillNames.length > 0 && skillNames.every((s: string) => s.trim().toLowerCase() === 'otros');
}

function getGenericIncompleteProfilePostText(): string {
  return '¡Hola! Estoy en Edify y me encantaría conectarme con profesionales de la construcción.\n\nSi necesitás colaboración en proyectos, sumar gente a tu equipo o conocer otros del rubro, escribime.';
}

function generateGenericProfilePostText(userData: any): string {
  if (!userData) {
    return getGenericIncompleteProfilePostText();
  }
  if (profilePostModalHasOnlyOtrosAsSkill(userData)) {
    return getGenericIncompleteProfilePostText();
  }

  const isCompany = userData.type === 'COMPANY';
  let name = '';
  if (isCompany) {
    name = userData.razon_social || userData.username || 'Mi empresa';
  } else {
    const firstName = userData.name || '';
    const lastName = userData.last_name || '';
    name = `${firstName} ${lastName}`.trim() || userData.username || 'Yo';
  }
  const description = userData.description || '';
  const skills = userData.skills || [];
  const validSkills = skills
    .map(profilePostModalGetSkillName)
    .filter((s: string) => s && s.trim() !== '' && s.trim().toLowerCase() !== 'otros');
  const skillNames = validSkills.slice(0, 3).join(', ');

  let text = `¡Hola! Soy ${name}`;
  if (isCompany) {
    text += ', una empresa';
  } else {
    text += ', un profesional';
  }
  text += '.\n\n';
  if (description && description.trim()) {
    text += `${description.trim()}\n\n`;
  }
  if (skillNames) {
    text += `Me especializo en ${skillNames}`;
    if (validSkills.length > 3) {
      text += ` y más`;
    }
    text += '.\n\n';
  }
  text += 'Estoy en Edify para conectarme con otros profesionales y proyectos. ¡Contáctame si necesitás mis servicios!';
  return text;
}

function formatProfilePostSuggestionLineBreaks(text: string): string {
  if (!text) return '';

  let normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  if (normalizedText.includes('\n')) {
    return normalizedText;
  }

  const sentences = normalizedText.split(/(?<=[.!?])\s+(?=[A-Z¿¡])/);

  if (sentences.length <= 2) {
    return normalizedText;
  }

  const paragraphs: string[] = [];
  let currentParagraph = '';

  sentences.forEach((sentence, index) => {
    const trimmed = sentence.trim();
    if (!trimmed) return;

    currentParagraph = currentParagraph ? currentParagraph + ' ' + trimmed : trimmed;

    if (index === sentences.length - 1) {
      paragraphs.push(currentParagraph);
    } else if (index > 0 && (index + 1) % 2 === 0) {
      paragraphs.push(currentParagraph);
      currentParagraph = '';
    }
  });

  return paragraphs.join('\n\n');
}

type ProfilePostSuggestionModalMode = 'ai_suggested' | 'composer';

interface ProfilePostSuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish?: () => void;
  userData?: any;
  mode?: ProfilePostSuggestionModalMode;
}

const ProfilePostSuggestionModal: React.FC<ProfilePostSuggestionModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  userData,
  mode = 'ai_suggested',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profileImage, initials, imageError } = useUserImage(userData ?? {});
  const completeName = userData?.type === 'COMPANY'
    ? (userData?.razon_social ?? '')
    : `${userData?.name ?? ''} ${userData?.last_name ?? ''}`.trim() || 'Usuario';
  const [suggestedText, setSuggestedText] = useState<string>('');
  const [editedText, setEditedText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [imageAttachment, setImageAttachment] = useState<PublicationAttachedImage | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [starterKitPickerOpen, setStarterKitPickerOpen] = useState(false);
  const [rateLimitNotice, setRateLimitNotice] = useState<string | null>(null);
  const fetchGenerationSeqRef = useRef(0);
  const editedTextRef = useRef(editedText);
  editedTextRef.current = editedText;

  const fetchSuggestedText = useCallback(async () => {
    const seq = ++fetchGenerationSeqRef.current;
    setIsLoading(true);
    setRateLimitNotice(null);
    const gate = canRequestProfilePostGeneration(userData?.id);
    if (gate.ok === false) {
      const genericText = generateGenericProfilePostText(userData);
      if (seq !== fetchGenerationSeqRef.current) return;
      setSuggestedText(genericText);
      setEditedText(genericText);
      setRateLimitNotice(
        `Ya generaste el máximo de textos con IA por ahora. Podés editar el texto de abajo o volver a generar en ${formatProfilePostRetryMinutes(gate.retryAfterMs)}.`
      );
      setIsLoading(false);
      return;
    }
    try {
      const text = await dispatch(generateProfilePostText() as any);
      if (seq !== fetchGenerationSeqRef.current) return;
      const formattedText = formatProfilePostSuggestionLineBreaks(text);
      setSuggestedText(formattedText);
      setEditedText(formattedText);
      recordSuccessfulProfilePostGeneration(userData?.id);
    } catch (err: any) {
      if (seq !== fetchGenerationSeqRef.current) return;
      const genericText = generateGenericProfilePostText(userData);
      setSuggestedText(genericText);
      setEditedText(genericText);
    } finally {
      if (seq === fetchGenerationSeqRef.current) setIsLoading(false);
    }
  }, [dispatch, userData]);

  useEffect(() => {
    if (!isOpen) {
      fetchGenerationSeqRef.current += 1;
      setSuggestedText('');
      setEditedText('');
      setRateLimitNotice(null);
      setImageAttachment(null);
      setVideoFile(null);
      setIsDragOver(false);
      setStarterKitPickerOpen(false);
      setImagePreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setVideoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      setIsLoading(false);
    } else if (isOpen && mode === 'ai_suggested' && !suggestedText) {
      fetchSuggestedText();
    }
  }, [isOpen, suggestedText, fetchSuggestedText, mode]);

  useEffect(() => {
    if (suggestedText && !editedTextRef.current) {
      setEditedText(suggestedText);
    }
  }, [suggestedText]);

  const handlePublish = async () => {
    if (!canPublishWithStarterRule({ text: editedText, image: imageAttachment, videoFile })) {
      dispatch(openMainModal({
        title: 'Completá la publicación',
        body: 'Si usás solo imágenes de ejemplo, agregá texto. Si subís una imagen o video propio, podés publicar sin texto.',
        iconType: 'error',
        isSuccess: false,
        confirmHandler: null,
      }));
      return;
    }

    setIsPublishing(true);
    try {
      await dispatch(createProfilePost({
        content: editedText.trim(),
        images: imageAttachment?.file || '',
        video: videoFile || '',
      }) as any);
      onClose();
      if (onPublish) {
        onPublish();
      } else {
        navigate('/marketplace/explorar');
      }
    } catch (err: any) {
      dispatch(openMainModal({
        title: 'Error al publicar',
        body: 'No pudimos publicar el posteo. Intentá nuevamente en unos minutos.',
        iconType: 'error',
        isSuccess: true,
        confirmHandler: null,
      }));
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = () => {
    fireAnalytics('profile_post_suggestion_cancelled', {
      edify_user_id: userData?.id ?? '',
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: 'publishing',
      action: 'profile_post_suggestion_cancelled',
    });
    setEditedText(suggestedText);
    onClose();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageAttachment({ file, origin: 'upload' });
      setImagePreview(URL.createObjectURL(file));
      setVideoFile(null);
      setVideoPreview(null);
    }
  };

  const handleStarterKitSelect = (file: File) => {
    setImageAttachment({ file, origin: 'starter' });
    setImagePreview(URL.createObjectURL(file));
    setVideoFile(null);
    setVideoPreview(null);
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setImageAttachment(null);
      setImagePreview(null);
    }
  };

  const removeMedia = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setImageAttachment(null);
    setImagePreview(null);
    setVideoFile(null);
    setVideoPreview(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isPublishing) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isPublishing) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      setImageAttachment({ file, origin: 'upload' });
      setImagePreview(URL.createObjectURL(file));
      setVideoFile(null);
      setVideoPreview(null);
    } else if (file.type.startsWith('video/')) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setImageAttachment(null);
      setImagePreview(null);
    }
  };

  const modalTitle =
    mode === 'composer' ? 'Nuevo posteo en tu muro' : 'Presentate en la plataforma';
  const modalDescription =
    mode === 'composer'
      ? 'Escribí lo que quieras compartir. Podés adjuntar una imagen propia, un video o elegir del catálogo.'
      : 'Generamos un texto sugerido para que te presentes. Es editable y muy útil para que otros usuarios te conozcan.';

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleCancel} preventClose={isPublishing || isLoading || starterKitPickerOpen}>
      <div className={styles.modal}>
        <h3 className={styles.title}>{modalTitle}</h3>
        <p className={styles.description}>{modalDescription}</p>
        {rateLimitNotice && (
          <p className={styles.rateLimitNotice} role="status">
            {rateLimitNotice}
          </p>
        )}
        <div className={styles.contentBodyWrapper}>
          <div className={styles.contentBody}>
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Generando texto sugerido...</p>
              </div>
            ) : (
              <div className={styles.postCard}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardAvatar}>
                    {!imageError && profileImage ? (
                      <img src={profileImage} alt="" className={styles.cardAvatar_img} />
                    ) : initials ? (
                      <div className={styles.cardAvatar_initials}>{initials}</div>
                    ) : (
                      <div className={styles.cardAvatar_initials}>U</div>
                    )}
                  </div>
                  <div className={styles.cardHeader_info}>
                    <p className={styles.cardHeader_name}>{completeName}</p>
                    <span className={styles.cardHeader_time}>Ahora</span>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  {imagePreview ? (
                    <div
                      className={`${styles.cardMedia} ${styles.cardMedia_withBlur}`}
                      style={{ '--preview-bg': `url(${imagePreview})` } as React.CSSProperties}
                    >
                      <img src={imagePreview} alt="" className={styles.cardMedia_content} />
                      <button
                        type="button"
                        className={styles.cardMedia_remove}
                        onClick={removeMedia}
                        disabled={isPublishing}
                        aria-label="Quitar foto"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : videoPreview ? (
                    <div className={styles.cardMedia}>
                      <video src={videoPreview} controls className={styles.cardMedia_video} />
                      <button
                        type="button"
                        className={styles.cardMedia_remove}
                        onClick={removeMedia}
                        disabled={isPublishing}
                        aria-label="Quitar video"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`${styles.cardMedia_add} ${isDragOver ? styles.cardMedia_add_dragOver : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className={styles.cardMedia_addTopRow}>
                        <p className={styles.cardMedia_addSpecs}>JPG, PNG hasta 5MB</p>
                        <button
                          type="button"
                          className={styles.cardMedia_catalogButton}
                          onClick={() => setStarterKitPickerOpen(true)}
                        >
                          Catálogo
                        </button>
                      </div>

                      <p className={styles.cardMedia_addText}>Elegí una imagen de ejemplo</p>

                      <StarterKitThumbnailSelector
                        mode="single"
                        onSelect={handleStarterKitSelect}
                        onOpenCatalog={() => setStarterKitPickerOpen(true)}
                        variant="inline"
                        showBorder={false}
                        hideHeader={true}
                        previewCount={9}
                      />

                      <div className={styles.cardMedia_addOr}>o</div>

                      <div className={styles.cardMedia_addActions}>
                        <label className={styles.cardMedia_addBtn}>
                          <input type="file" accept="image/*" onChange={handleImageChange} disabled={isPublishing} />
                          <ImageIcon size={24} />
                          <span>Subir imagen propia</span>
                        </label>
                        <label className={styles.cardMedia_addBtn}>
                          <input type="file" accept="video/*" onChange={handleVideoChange} disabled={isPublishing} />
                          <Video size={24} />
                          <span>Subir video</span>
                        </label>
                      </div>

                      <p className={styles.cardMedia_addHint}>Arrastrá un archivo o elegí una opción.</p>
                    </div>
                  )}
                  <div className={styles.cardText}>
                    <TextareaAutosize
                      className={styles.cardTextarea}
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      placeholder={
                        mode === 'composer'
                          ? '¿Qué querés compartir?'
                          : 'Escribí tu presentación...'
                      }
                      minRows={5}
                      disabled={isPublishing}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {!isLoading && (
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancel}
              disabled={isPublishing || isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.publishButton}
              onClick={handlePublish}
              disabled={
                isPublishing ||
                isLoading ||
                !canPublishWithStarterRule({ text: editedText, image: imageAttachment, videoFile })
              }
            >
              {isPublishing ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        )}
        <StarterKitImagePicker
          isOpen={starterKitPickerOpen}
          onClose={() => setStarterKitPickerOpen(false)}
          onSelect={handleStarterKitSelect}
          mode="single"
        />
      </div>
    </ModalOverlay>
  );
};

export default ProfilePostSuggestionModal;
